import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/base/SEOMeta';
import ScrollToTop from '@/components/base/ScrollToTop';
import { aboutData } from '@/mocks/data';

export default function AboutPage() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于黄瓜tv',
    description: aboutData.intro,
    about: {
      '@type': 'Organization',
      name: aboutData.brandName,
      description: aboutData.intro,
      url: 'https://huanggua.tv',
      contactPoint: {
        '@type': 'ContactPoint',
        email: aboutData.contact.email,
        telephone: aboutData.contact.phone,
        contactType: 'customer service',
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: '黄瓜路88号黄瓜大厦',
        addressLocality: '北京',
        addressCountry: 'CN',
      },
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '黄瓜tv是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '黄瓜tv是一款高清视频在线观看平台，提供海量高清影视资源、流畅播放体验与丰富内容分类，包括电视剧、电影、综艺、动漫、短剧、纪录片等多种类型。',
        },
      },
      {
        '@type': 'Question',
        name: '黄瓜tv需要付费吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '黄瓜tv提供大量免费内容供用户观看，同时也有会员专享的高品质内容。用户可以根据自己的需求选择免费观看或订阅会员服务。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系黄瓜tv客服？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过以下方式联系我们：邮箱 contact@huanggua.tv，电话 400-888-6666，或关注我们的官方微信"黄瓜tv官方"和微博@黄瓜tv。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title="关于我们"
        description="黄瓜tv - 高清视频在线观看平台。了解黄瓜tv的品牌故事、发展历程、用户口碑和联系方式。累计用户2.8亿+，海量高清影视资源等你来体验。"
        keywords="黄瓜tv,黄瓜tv官网入口,黄瓜tv在线观看,关于黄瓜tv,视频平台"
        canonicalUrl="https://huanggua.tv/about"
        schema={{ ...aboutSchema, ...faqSchema }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <Breadcrumb items={[
            { label: '首页', path: '/' },
            { label: '关于我们' },
          ]} />

          {/* Hero */}
          <section className="w-full px-4 md:px-6 py-16 md:py-24 bg-gradient-to-b from-background-50 to-background-100">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-3xl md:text-5xl font-bold text-foreground-950 font-heading mb-4">
                关于<span className="text-primary-500">黄瓜tv</span>
              </h1>
              <p className="text-lg text-foreground-600 leading-relaxed">{aboutData.intro}</p>
            </div>
          </section>

          {/* Stats */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {aboutData.stats.map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="text-3xl md:text-4xl font-bold text-primary-500 font-heading mb-2">{stat.value}</div>
                  <div className="text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Mission & Vision */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="p-8 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-4">
                  <i className="ri-flag-line text-xl"></i>
                </div>
                <h2 className="text-xl font-bold text-foreground-950 font-heading mb-3">我们的使命</h2>
                <p className="text-foreground-600 leading-relaxed">{aboutData.mission}</p>
              </div>
              <div className="p-8 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mb-4">
                  <i className="ri-eye-line text-xl"></i>
                </div>
                <h2 className="text-xl font-bold text-foreground-950 font-heading mb-3">我们的愿景</h2>
                <p className="text-foreground-600 leading-relaxed">{aboutData.vision}</p>
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading text-center mb-12">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-0.5"></div>
                <div className="flex flex-col gap-8">
                  {aboutData.milestones.map((milestone, idx) => (
                    <div
                      key={milestone.year}
                      className={`relative flex items-start gap-6 md:gap-10 ${
                        idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-12 md:pl-0`}>
                        <div className="p-4 rounded-xl bg-background-100 border border-background-200/70 inline-block">
                          <span className="text-sm font-bold text-primary-500">{milestone.year}</span>
                          <p className="text-sm text-foreground-700 mt-1">{milestone.event}</p>
                        </div>
                      </div>
                      <div className="absolute left-2.5 md:left-1/2 md:-translate-x-1/2 w-3.5 h-3.5 rounded-full bg-primary-500 border-4 border-background-50 mt-3"></div>
                      <div className="flex-1 hidden md:block"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading text-center mb-10">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                {aboutData.testimonials.map((t) => (
                  <div key={t.name} className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                      {Array.from({ length: 5 - t.rating }).map((_, i) => (
                        <i key={i} className="ri-star-line text-foreground-300 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">&ldquo;{t.content}&rdquo;</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-sm font-bold">
                        {t.name.charAt(0)}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                        <div className="text-xs text-foreground-500">{t.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section id="contact" className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading text-center mb-10">
                联系我们
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mx-auto mb-3">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                  <a href={`mailto:${aboutData.contact.email}`} className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
                    {aboutData.contact.email}
                  </a>
                </div>
                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mx-auto mb-3">
                    <i className="ri-phone-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服电话</h3>
                  <a href={`tel:${aboutData.contact.phone}`} className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
                    {aboutData.contact.phone}
                  </a>
                </div>
                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mx-auto mb-3">
                    <i className="ri-map-pin-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h3>
                  <p className="text-sm text-foreground-600">{aboutData.contact.address}</p>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}